import { normalizeColorLabel } from '@/lib/color-url';
import { useStyle } from '@/classify';
import { useIsPreorder } from '@/common/hooks/use-is-preorder';
// import { Credenza, CredenzaBody, CredenzaContent, CredenzaTrigger } from '@/components/ui/credenza';
import { placeHolderImage } from '@/constants/placeHolderImage';
import { useGetProduct } from '@/hooks/Product/use-get-product';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { formatString } from '@/lib/format-string';
import { cn } from '@/lib/utils';
import { ColorItem, ConfigurableProduct, Maybe } from '@/types/graphql.prod';
import { BrowserPersistence } from '@/util';
import resourceUrl from '@/util/makeUrl';
import { Plus } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import LoadingIndicator from '../LoadingIndicator';
import Price from '../Price';
import { getSortedColor } from '../ProductFullDetail/get-sorted-color';
// import { MobileAddToCartSuccessTipQuickView } from '../ProductFullDetail/mobile-addtocart-success-tip-quick-view';
import { pushEcommerceEvent } from '@/lib/data-layer';
import { ProductFullDetailQuickView } from '../ProductFullDetail/product-full-detail-quick-view';
import Rating from '../Rating';
import { Button } from '../ui/button';
import { CustomizeOptions } from './CustomizeOptions/customizeOptions';
import defaultClasses from './item.module.css';
import GalleryItemShimmer from './item.shimmer';

export const GalleryItem = ({
  item,
  showCustomizeOptions = true,
  isShowTags = true,
  colorFilters = [],
  categoryId,
  categoryName,
}: {
  item: ConfigurableProduct;
  showCustomizeOptions?: boolean;
  isShowTags?: boolean;
  colorFilters?: any;
  categoryId?: string;
  categoryName?: string;
}) => {
  const allColorCount = useMemo(() => {
    if (item.configurable_options) {
      const colors = item.configurable_options.find(temp => temp?.attribute_code === 'lw_color')?.values;

      return colors?.length ?? 0;
    }

    return 0;
  }, [item]);

  const classes = useStyle(defaultClasses);
  const { isMobile } = useIsMobile();
  const storage = new BrowserPersistence();
  const {
    name,
    serial_name,
    price_range,
    small_image,
    url_key,
    rating_summary,
    review_count,
    hover_image,
    prices,
    product_mark,
    configurable_options,
    show_colorsV1: colorOptions,
  } = item || {};
  const findMatchingValue = (colorFilters: any, values: any) => {
    // 将values数组转换为以value_index为键的映射，提高查找效率
    const valueMap = new Map();
    values.forEach((item: { value_index: { toString: () => any } }) => {
      valueMap.set(item.value_index.toString(), item);
    });

    // 从colorFilters数组末尾开始向前遍历
    for (let i = colorFilters.length - 1; i >= 0; i--) {
      const filterValue = colorFilters[i];
      if (valueMap.has(filterValue)) {
        return valueMap.get(filterValue);
      }
    }

    // 如果没有找到任何匹配项，返回null
    return null;
  };
  const [selectedColor, setSelectedColor] = useState<Maybe<ColorItem> | null>(() => {
    if (
      colorFilters &&
      colorFilters.length &&
      item.configurable_options?.find(temp => temp?.attribute_code === 'lw_color')?.values
    ) {
      const current = findMatchingValue(
        colorFilters,
        item.configurable_options?.find(temp => temp?.attribute_code === 'lw_color')?.values,
      );
      const label = current?.label;
      return colorOptions?.find((x: ColorItem) => x.label === label) || null;
    }
    return colorOptions ? colorOptions.find((x: ColorItem) => x.is_selected === 1) : null;
  });
  // console.log(
  //   'selectedColor',
  //   colorFilters,
  //   colorOptions,
  //   item.configurable_options?.find(temp => temp?.attribute_code === 'lw_color')?.values,
  //   selectedColor,
  // );
  const [galleryImagesHoverPC, setGalleryImagesHoverPC] = useState<string>(
    (selectedColor?.hover_image || hover_image) ?? '',
  );
  const [open, setOpen] = useState(false);
  const { isHasPreOrder } = useIsPreorder({
    product: item,
    configurable_options,
  });
  const outOfStock = useMemo(() => {
    if (isHasPreOrder) {
      return false;
    }

    if (item.stock_status === 'OUT_OF_STOCK') {
      return true;
    }
  }, [isHasPreOrder, item.stock_status]);

  const finalPrice = prices?.finalPrice?.value;
  const regularPrice = prices?.regularPrice?.value;
  const hasSpecialPrice = useMemo(() => {
    if (!finalPrice || !regularPrice) {
      return false;
    }

    return finalPrice < regularPrice;
  }, [finalPrice, regularPrice]);

  const smallImageURL = small_image?.url;
  const [galleryImagesPC, setGalleryImagesPC] = useState<string>((selectedColor?.image || smallImageURL) ?? '');
  const storeCode = storage.getItem('store_view_code') || process.env.NEXT_PUBLIC_STORE_VIEW_CODE;

  const productLink = useMemo(() => {
    const basePath = storeCode === 'hisea_de' ? '/de' : '';
    const colorPath = selectedColor ? `-${normalizeColorLabel(selectedColor.label)}` : '';

    return resourceUrl(`${basePath}/${url_key}${colorPath}.html`);
  }, [selectedColor, storeCode, url_key]);

  // fallback to regular price when final price is unavailable
  const priceSource = price_range?.minimum_price?.final_price || price_range?.minimum_price?.regular_price;

  const maxPriceSource = price_range?.maximum_price?.final_price || price_range?.maximum_price?.regular_price;

  // Hide the Rating component until it is updated with the new look and feel (PWA-2512).
  const ratingAverage =
    rating_summary !== null && rating_summary !== undefined ? (
      <Rating
        rating={rating_summary}
        disabled
      />
    ) : null;

  const maybeReviewCount =
    review_count > 0 ? <div className={cn(classes.maybeReviewCount)}>({review_count})</div> : null;

  const maybeNew =
    product_mark && product_mark.toLowerCase().indexOf('new') > -1 && !outOfStock ? (
      <p className='rounded-[3px] border-1 border-black bg-black px-[5px] font-roboto-medium text-[12px] font-medium leading-[18px] text-white lg_text-[16px] lg_leading-[23px]'>
        New
      </p>
    ) : null;

  const maybeBestseller =
    product_mark && product_mark.toLowerCase().indexOf('best seller') > -1 && !outOfStock ? (
      <p className='rounded-[3px] bg-[#F86949] px-[5px] font-roboto-medium text-[12px] font-medium leading-[20px] text-white lg_text-[16px] lg_leading-[25px]'>
        Best Seller
      </p>
    ) : null;

  const maybeOutOfStockMaskRed = outOfStock ? (
    <p className='rounded-[3px] bg-[#E0E0E0] px-[5px] font-roboto-medium text-[12px] font-medium leading-[20px] text-[#4C4C4C] lg_text-[16px] lg_leading-[25px]'>
      Out of Stock
    </p>
  ) : null;

  if (!item) {
    return <GalleryItemShimmer />;
  }

  const handleSelectItem = () => {
    pushEcommerceEvent({
      event: 'select_item',
      ecommerce: {
        item_list_id: categoryName || '',
        item_list_name: categoryName || '',
        items: [
          {
            item_name: name,
            item_id: item.sku,
            price: finalPrice,
            currency: prices?.finalPrice?.currency || 'USD',
            item_category: categoryName || '',
            quantity: 1,
          },
        ],
      },
    });
  };

  return (
    <div
      className={cn('flex flex-col', 'hover_shadow', 'relative', 'group')}
      aria-live='polite'
      aria-busy='false'
    >
      <div className='relative'>
        <Link
          href={productLink}
          onClick={handleSelectItem}
          className={'group relative w-full overflow-hidden'}
        >
          {galleryImagesPC && (
            <Image
              width={340}
              height={430}
              src={galleryImagesPC}
              quality={100}
              className={cn('duration-300 w-full bg-f9f9f9 transition-opacity', !isMobile && 'group-hover_opacity-0')}
              alt={name ?? ''}
              placeholder={placeHolderImage}
            />
          )}
          {galleryImagesHoverPC && !isMobile && (
            <span className='duration-300 absolute inset-0 block bg-f9f9f9 opacity-0 transition-opacity group-hover_opacity-100'>
              <Image
                width={340}
                height={430}
                src={galleryImagesHoverPC}
                quality={100}
                className='w-full'
                alt={name ?? ''}
                placeholder={placeHolderImage}
              />
            </span>
          )}
        </Link>
      </div>

      <div className={cn(classes.infoBox)}>
        {showCustomizeOptions && colorOptions && colorOptions?.length > 0 && (
          <CustomizeOptions
            allColorCount={allColorCount - colorOptions?.length}
            colorOptions={colorOptions}
            selectedColor={selectedColor}
            setSelectedColor={setSelectedColor}
            setGalleryImagesPC={setGalleryImagesPC}
            setGalleryImagesHoverPC={setGalleryImagesHoverPC}
            url_key={url_key ?? ''}
          />
        )}
        <Link
          href={productLink}
          onClick={handleSelectItem}
          className={classes.serialName}
        >
          <span>{formatString(serial_name ?? '') || ''}</span>
        </Link>
        <Link
          href={productLink}
          onClick={handleSelectItem}
          className={cn(classes.name)}
        >
          <span className='line-clamp-2'>{formatString(name ?? '')}</span>
        </Link>
        <div className={classes.rating}>
          {ratingAverage}
          {maybeReviewCount}
        </div>
        <div className={classes.bottom}>
          <div className={classes.priceAll}>
            <div className={classes.price}>
              <Price
                value={finalPrice}
                currencyCode={priceSource?.currency}
              />
            </div>

            {hasSpecialPrice && (
              <div className={classes.price_delete}>
                <Price
                  value={regularPrice}
                  currencyCode={maxPriceSource?.currency ?? 'USD'}
                />
              </div>
            )}
          </div>
          {isMobile && (
            <AddCartBtn
              open={open}
              setOpen={setOpen}
            />
          )}
        </div>
        {!isMobile && (
          <AddCartBtn
            open={open}
            setOpen={setOpen}
          />
        )}
      </div>
      <div className='absolute left-0 top-0 flex w-full flex-col items-start justify-start gap-[5px] p-[4px] lg_flex-row lg_items-center lg_gap-[8px] lg_p-[10px]'>
        {isShowTags && maybeBestseller}
        {isShowTags && maybeNew}
        {hasSpecialPrice && regularPrice && finalPrice && (
          <p className='rounded-[3px] border-1 border-black bg-white px-[5px] font-roboto-medium text-[12px] font-medium leading-[18px] lg_text-[16px] lg_leading-[23px]'>
            {Math.round(((regularPrice - finalPrice) / regularPrice) * 100)}%&nbsp;Off
          </p>
        )}
        {isShowTags && maybeOutOfStockMaskRed}
      </div>
      {/* {hasSpecialPrice && regularPrice && finalPrice && (
        <div className={isMobile ? classes.triangle_m : classes.triangle}>
          <span className={isMobile ? classes.triangle_text_m : classes.triangle_text}>
            - {Math.round(((regularPrice - finalPrice) / regularPrice) * 100)}%
          </span>
        </div>
      )} */}
      <HoverQuickView
        open={open}
        setOpen={setOpen}
        newUrlKey={item?.url_key ?? ''}
        classes={classes}
        productLink={productLink}
        selectedColor={selectedColor}
        galleryItem={item}
      />
      <HoverMask
        open={open}
        setOpen={setOpen}
      />
    </div>
  );
};

export function AddCartBtn({ open, setOpen }: { open: boolean; setOpen: any }, classes = defaultClasses) {
  const { isMobile } = useIsMobile();
  return (
    <>
      {isMobile ? (
        <Button
          variant={'outline'}
          className={cn(
            ' h-[25px] w-[65px] rounded-[25px] px-0',
            //Google Analytics Trigger Classes
            'quick-add-to-cart',
          )}
          style={{ borderWidth: '1px' }}
          onClick={() => setOpen(!open)}
        >
          <Plus
            className='mr-[3px]'
            size={14}
          />
          <span className='font-roboto-medium text-[14px]'>Add</span>
        </Button>
      ) : (
        <Button
          variant={'outline'}
          className={cn(
            ' h-[50px] w-[98px] rounded-[25px] border-1',
            //Google Analytics Trigger Classes
            'quick-add-to-cart',
          )}
          style={{ borderWidth: '1px' }}
          onClick={() => setOpen(!open)}
        >
          <Plus
            className='mr-[5px]'
            size={16}
          />
          <span className='font-roboto-medium text-[18px]'>Add</span>
        </Button>
      )}
    </>
  );
}

export function HoverQuickView({
  newUrlKey,
  open = false,
  setOpen,
  classes,
  productLink,
  selectedColor,
  galleryItem,
}: {
  newUrlKey: string;
  open: boolean;
  setOpen: any;
  classes: any;
  productLink: string;
  selectedColor: any;
  galleryItem: any;
}) {
  // const [open, setOpen] = useState(false);
  const [seoColorLabel, setSeoColorLabel] = useState('');
  const [quickViewSuccessOpen, setQuickViewSuccessOpen] = useState(false);
  const [quickViewSuccessNumber, setQuickViewSuccessNumber] = useState(1);

  const { loading, product } = useGetProduct({
    originalUrlKey: newUrlKey,
    skip: !open,
  });
  const defaultValues = useMemo(() => {
    const result = galleryItem?.configurable_options?.reduce(
      (acc: Record<string, any>, opts: any) => {
        if (opts?.attribute_code?.toLowerCase().startsWith('color')) {
          const sortedColor = getSortedColor({
            values: opts?.values,
            variants: product.variants,
            attribute_code: opts?.attribute_code,
          });
          const firstColor = sortedColor[0];

          acc[opts?.attribute_code] = {
            uid: firstColor?.uid,
            label: firstColor?.label,
            option_attribute_id: opts?.attribute_id,
            option_selection_value: firstColor?.value_index,
          };
        }

        if (opts?.label?.toLowerCase().startsWith('size')) {
          acc[opts?.attribute_code] = undefined;
        }
        if (opts?.attribute_code?.toLowerCase().startsWith('lw_color') && selectedColor) {
          // console.log('selectedOption1', opts.values, selectedColor);
          const selectedOption = opts?.values?.find(v => v.label === selectedColor?.label);
          // console.log('selectedOption2', selectedOption);
          acc[opts?.attribute_code] = {
            uid: selectedOption?.uid,
            label: selectedOption?.label,
            option_attribute_id: opts?.attribute_id,
            option_selection_value: selectedOption?.value_index,
          };
        }
        return acc;
      },
      {
        quantity: '1',
      },
    );
    // console.log('defaultValues', result);
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const handleColorLabelChange = (colorLabel: string) => {
    if (colorLabel === seoColorLabel) {
      return;
    }

    setSeoColorLabel(colorLabel);
  };

  const { isMobile } = useIsMobile();
  return (
    <div className={cn(isMobile ? classes.productDetailBoxM : classes.productDetailBox, open ? '' : 'hidden')}>
      <div
        className={cn(
          'flex h-[50px] items-center justify-between pr-[12px] lg_h-[46px] lg_pr-0',
          classes.productDetailBoxHeader,
          isMobile ? 'fixed left-0 top-0 z-[100] w-full bg-white' : '',
        )}
      >
        <Image
          src={`/icons/close.svg`}
          width={22}
          height={22}
          alt='close'
          onClick={() => setOpen(false)}
          className='ml-auto cursor-pointer'
        />
      </div>
      <ProductWrapper
        loading={loading}
        product={product}
        defaultValues={defaultValues}
        handleColorLabelChange={handleColorLabelChange}
        seoColorLabel={seoColorLabel}
        setOpen={setOpen}
        setQuickViewSuccessOpen={setQuickViewSuccessOpen}
        setQuickViewSuccessNumber={setQuickViewSuccessNumber}
        productLink={productLink}
      />

      {/* <Credenza
        open={open}
        onOpenChange={setOpen}
      >
        <CredenzaTrigger asChild>
          {isMobile ? (
            <Button
              variant={'outline'}
              className={'h-[25px] w-[65px] rounded-[25px] px-0'}
              style={{ borderWidth: '1px' }}
            >
              <Plus
                className='mr-[3px]'
                size={14}
              />
              <span className='font-roboto-medium text-[14px]'>Add</span>
            </Button>
          ) : (
            <Button
              variant={'outline'}
              className={'h-[50px] w-[98px] rounded-[25px] border-1'}
              style={{ borderWidth: '1px' }}
            >
              <Plus
                className='mr-[5px]'
                size={16}
              />
              <span className='font-roboto-medium text-[18px]'>Add</span>
            </Button>
          )}
        </CredenzaTrigger>
        <CredenzaContent className='max-h-[calc(100vh-200px)] max-w-[1100px] bg-white'>
          <CredenzaBody className='lg_pb-4m h-fit space-y-4 overflow-auto px-0 pb-20 text-sm sm_pb-0 sm_text-left lg_max-h-[680px]'>
            <ProductWrapper
              loading={loading}
              product={product}
              defaultValues={defaultValues}
              handleColorLabelChange={handleColorLabelChange}
              seoColorLabel={seoColorLabel}
              setOpen={setOpen}
              setQuickViewSuccessOpen={setQuickViewSuccessOpen}
              setQuickViewSuccessNumber={setQuickViewSuccessNumber}
            />
          </CredenzaBody>
        </CredenzaContent>
      </Credenza>
      <MobileAddToCartSuccessTipQuickView
        addToCartStatus={quickViewSuccessOpen}
        addToCartQty={quickViewSuccessNumber}
        setQuickViewSuccessOpen={setQuickViewSuccessOpen}
      /> */}
    </div>
  );
}

export function HoverMask({ open = false, setOpen }: { open: boolean; setOpen: any }) {
  return (
    <div
      className={cn('fixed left-0 top-0 z-[39] h-full w-full bg-black bg-opacity-0', open ? '' : 'hidden')}
      onClick={() => setOpen(false)}
    />
  );
}

function ProductWrapper({
  loading,
  product,
  defaultValues,
  handleColorLabelChange,
  seoColorLabel,
  setOpen,
  setQuickViewSuccessOpen,
  setQuickViewSuccessNumber,
  productLink,
}: any) {
  if (loading) {
    return (
      <div className='flex min-h-[80vh] items-center justify-center lg_min-h-[680px]'>
        <LoadingIndicator />
      </div>
    );
  }

  if (!product) {
    return <div className='min-h-[80vh] lg_min-h-[680px] ' />;
  }

  return (
    <ProductFullDetailQuickView
      product={product}
      defaultValues={defaultValues}
      handleColorLabelChange={handleColorLabelChange}
      seoColorLabel={seoColorLabel}
      setOpen={setOpen}
      setQuickViewSuccessOpen={setQuickViewSuccessOpen}
      setQuickViewSuccessNumber={setQuickViewSuccessNumber}
      productLink={productLink}
    />
  );
}

export default GalleryItem;
